import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import axios from "axios";
import { Download, Copy, Check, Mail, Building2, ArrowRight } from "lucide-react";
import { FaXTwitter, FaFacebookF, FaWhatsapp } from "react-icons/fa6";
import { ShareCard } from "./ShareCard";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "whatsmydataworth.com.au";

const MONETISE = [
  { t: "Targeted ads", d: "Your taps, searches and location build a profile advertisers bid on in milliseconds." },
  { t: "Data brokers", d: "Loyalty, purchase and account data is packaged and resold to hundreds of third parties." },
  { t: "AI & personalisation", d: "Your behaviour trains recommendation and ad-ranking models that keep you scrolling." },
];

export default function ResultPage({ amount, result, quiz }) {
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [saving, setSaving] = useState(false);

  const shareText = `My data is worth A$${amount}/yr — what's yours? ${SITE}`;

  const downloadCard = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.download = "my-data-worth.png";
      a.href = dataUrl;
      a.click();
      trackEvent("share_result", { method: "save_card" });
      toast.success("Share card saved 🎉");
    } catch {
      toast.error("Couldn't generate image. Try again.");
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(shareText);
    setCopied(true);
    trackEvent("share_result", { method: "copy_link" });
    toast.success("Link copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const openShare = (net) => {
    const t = encodeURIComponent(shareText);
    const u = encodeURIComponent(`https://${SITE}`);
    const urls = {
      x: `https://twitter.com/intent/tweet?text=${t}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${t}`,
      whatsapp: `https://wa.me/?text=${t}`,
    };
    window.open(urls[net], "_blank", "noopener,noreferrer");
    trackEvent("share_result", { method: net });
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSaving(true);
    try {
      await axios.post(`${API}/lead`, { email, amount, quiz, result });
      setSubscribed(true);
      trackEvent("email_captured", { value: amount, currency: "AUD" });
      toast.success("Done! Your breakdown is on the way.");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      data-testid="result-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col gap-8 pb-16"
    >
      <div>
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-zinc-500">The verdict</p>
        <h2 className="mt-1 font-display text-3xl font-black tracking-tight text-zinc-900">
          <span className="text-gradient">A${amount.toLocaleString()}</span>
          <span className="text-zinc-400">/yr</span>
        </h2>
        <p className="mt-1 text-zinc-600">is being earned from your data.</p>
      </div>

      {/* Companies stat */}
      <div
        data-testid="companies-stat"
        className="flex items-center gap-4 rounded-2xl border border-gold/40 bg-gold/10 p-5"
      >
        <span className="text-gradient font-display text-4xl font-black leading-none">
          ~{result.companies}
        </span>
        <p className="text-sm text-zinc-700">
          <span className="font-semibold text-zinc-900">companies</span> could be buying, packaging
          and re-selling your data across the ad &amp; broker ecosystem.
        </p>
      </div>

      <ShareCard ref={cardRef} amount={amount} />

      {/* Share actions */}
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <button
            data-testid="download-card-btn"
            onClick={downloadCard}
            className="flex items-center justify-center gap-2 rounded-full bg-lime py-3.5 font-display font-bold text-black shadow-[0_8px_22px_rgba(204,255,0,0.45)] transition-transform active:scale-95"
          >
            <Download className="h-5 w-5" strokeWidth={2.5} /> Save card
          </button>
          <button
            data-testid="copy-link-btn"
            onClick={copyLink}
            className="flex items-center justify-center gap-2 rounded-full border border-black/10 bg-white py-3.5 font-display font-bold text-zinc-900 shadow-sm transition-transform active:scale-95"
          >
            {copied ? <Check className="h-5 w-5 text-limeink" /> : <Copy className="h-5 w-5" />}
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { net: "x", Icon: FaXTwitter, label: "X" },
            { net: "facebook", Icon: FaFacebookF, label: "Facebook" },
            { net: "whatsapp", Icon: FaWhatsapp, label: "WhatsApp" },
          ].map(({ net, Icon, label }) => (
            <button
              key={net}
              data-testid={`share-${net}-btn`}
              onClick={() => openShare(net)}
              className="flex items-center justify-center gap-2 rounded-full border border-black/10 bg-white py-3 text-sm text-zinc-700 shadow-sm transition-transform active:scale-95 hover:border-aqua"
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
      </div>

      {/* Breakdown */}
      <div data-testid="breakdown" className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm">
        <h3 className="font-display text-lg font-bold text-zinc-900">Your breakdown</h3>
        <div className="mt-4 flex flex-col gap-3">
          {result.breakdown.map((b) => (
            <div key={b.key} className="flex items-center justify-between text-sm">
              <span className="text-zinc-500">{b.label}</span>
              <span className="font-semibold text-zinc-900">A${b.value}</span>
            </div>
          ))}
          <div className="my-1 h-px bg-black/10" />
          <div className="flex items-center justify-between text-sm text-zinc-500">
            <span>Base value</span>
            <span>A${result.subtotal}</span>
          </div>
          {result.multipliers.map((m) => (
            <div key={m.key} className="flex items-center justify-between text-sm text-zinc-500">
              <span>{m.label}</span>
              <span className="font-semibold text-aquaink">×{m.value.toFixed(2)}</span>
            </div>
          ))}
          <div className="mt-1 flex items-center justify-between">
            <span className="font-display font-bold text-zinc-900">Total / year</span>
            <span className="text-gradient font-display text-xl font-black">
              A${amount.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* Email capture */}
      <div className="rounded-2xl border border-aqua/40 bg-aqua/10 p-5">
        {subscribed ? (
          <div data-testid="email-success" className="flex items-center gap-3">
            <Check className="h-6 w-6 text-limeink" />
            <p className="text-sm text-zinc-700">
              Sweet — check your inbox for the full breakdown & claw-back tips.
            </p>
          </div>
        ) : (
          <form onSubmit={submitEmail} className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-aquaink" />
              <p className="font-display font-semibold text-zinc-900">
                Email me my breakdown + how to claw some back
              </p>
            </div>
            <div className="flex gap-2">
              <input
                data-testid="email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="flex-1 rounded-full border border-black/15 bg-white px-4 py-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-aquaink focus:outline-none"
              />
              <button
                data-testid="email-submit-btn"
                type="submit"
                disabled={saving}
                className="flex items-center gap-1 rounded-full bg-aqua px-5 py-3 font-display text-sm font-bold text-black disabled:opacity-60"
              >
                {saving ? "…" : <ArrowRight className="h-5 w-5" strokeWidth={2.5} />}
              </button>
            </div>
            <p className="text-xs text-zinc-500">Optional. We never sell your data — the irony isn't lost on us.</p>
          </form>
        )}
      </div>

      {/* Explainer */}
      <div>
        <h3 className="font-display text-lg font-bold text-zinc-900">How companies monetise you</h3>
        <div className="mt-4 flex flex-col gap-3">
          {MONETISE.map((m, i) => (
            <div key={m.t} className="rounded-2xl border border-black/5 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-lime/30 font-display text-xs font-bold text-limeink">
                  {i + 1}
                </span>
                <p className="font-display font-semibold text-zinc-900">{m.t}</p>
              </div>
              <p className="mt-1.5 pl-8 text-sm text-zinc-500">{m.d}</p>
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs leading-relaxed text-zinc-400">
        Anchored to published ARPU disclosures (Meta, Alphabet), Australian loyalty scheme economics
        and data-broker rate research. Estimates only.
      </p>

      {/* Business footer link */}
      <a
        data-testid="business-footer-link"
        href="https://dollars4data.net/value-my-data"
        target="_blank"
        rel="noopener noreferrer"
        className="group flex items-center justify-between rounded-2xl border border-black/10 bg-white p-4 shadow-sm transition-colors hover:border-gold"
      >
        <div className="flex items-center gap-3">
          <Building2 className="h-5 w-5 text-gold" />
          <p className="text-sm text-zinc-600">
            Own a business? Your customer database is worth thousands
          </p>
        </div>
        <ArrowRight className="h-4 w-4 text-zinc-400 transition-transform group-hover:translate-x-1" />
      </a>
    </motion.div>
  );
}
